import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Check, Crown, Zap, Sparkles, Smartphone, Gift, ShieldCheck, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const ICONS = {
  freemium: Zap,
  elite: Crown,
};

export default function PlansPage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: plansData }, { data: statsData }] = await Promise.all([
        api.get('/plans'),
        api.get('/plans/stats'),
      ]);
      setPlans(plansData.plans || []);
      setCurrent(plansData.current_plan);
      setStats(statsData);
    } catch (e) { console.warn('Failed to load plans', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upgrade = async (code) => {
    if (!user) { nav('/auth'); return; }
    if (upgrading) return;
    setUpgrading(true);
    try {
      if (code === 'elite') {
        // Redirect to Stripe Checkout
        const { data: co } = await api.post('/payments/checkout', {
          lookup_key: 'elite_lifetime',
          origin_url: window.location.origin,
        });
        if (co?.checkout_url) {
          window.location.href = co.checkout_url;
          return;
        }
        toast.error('No se pudo iniciar el pago');
      } else {
        await api.post('/plans/upgrade', { plan_code: code });
        toast.success('Plan actualizado');
        if (refresh) await refresh();
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar el plan');
    } finally { setUpgrading(false); }
  };

  if (loading) return <div className="min-h-screen bg-aura-bg grid place-items-center text-4xl text-aura-textDim animate-aura-pulse">⚡</div>;

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative overflow-x-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-aura-gold/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-aura-violet/20 blur-3xl" />

      <header className="relative flex items-center justify-between px-5 pt-6 md:px-10">
        <button onClick={() => nav(user ? '/home' : '/')} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">PAQUETES AURA</div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-5xl px-5 md:px-10 pt-6 space-y-8">
        {/* Hero */}
        <section className="text-center" data-testid="plans-hero">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 mb-4">
            <Sparkles size={14} className="text-aura-gold" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-display font-semibold text-white/70">
              Elige tu experiencia AURA
            </span>
          </div>
          <h1 className="font-display font-black text-white text-4xl sm:text-5xl md:text-6xl leading-[0.95]">
            Dos formas de<br />
            <span className="aura-text-gradient">farmear Aura</span>.
          </h1>
          <p className="mt-4 text-aura-textDim max-w-xl mx-auto text-sm md:text-base">
            Empieza gratis con FREEMIUM o pasa a ELITE con un único pago para participar en competencias por puntos y ganar smartphones y gadgets cada mes.
          </p>

          {stats && (
            <div className="mt-6 flex justify-center gap-3 flex-wrap" data-testid="plans-stats">
              <StatChip label="Freemium" value={stats.freemium} color="#00F0FF" />
              <StatChip label="ELITE activos" value={stats.elite_active} color="#FFD166" />
              <StatChip label="ELITE pendientes" value={stats.elite_pending} color="#B026FF" />
            </div>
          )}
        </section>

        {/* Pricing Cards */}
        <section className="grid md:grid-cols-2 gap-4 md:gap-6" data-testid="plans-grid">
          {plans.map((p) => (
            <PlanCard
              key={p.code}
              plan={p}
              current={current === p.code}
              icon={ICONS[p.code]}
              upgrading={upgrading}
              onUpgrade={() => upgrade(p.code)}
              testId={`plan-card-${p.code}`}
            />
          ))}
        </section>

        {/* Prize teaser (ELITE) */}
        <section className="aura-card p-6 md:p-8 relative overflow-hidden" data-testid="prize-teaser">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-aura-gold/25 blur-3xl" />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="text-[10px] uppercase tracking-[0.35em] font-display font-black text-aura-gold">
                Premios ELITE · competencias por puntos
              </div>
              <h2 className="mt-2 font-display font-black text-white text-3xl md:text-4xl leading-[0.95]">
                Aspira a ganar <span className="text-aura-gold">smartphones</span><br />
                y <span className="text-aura-gold">gadgets</span> cada mes.
              </h2>
              <p className="mt-3 text-sm text-aura-textDim max-w-lg">
                Con un único pago de <b className="text-white">$9 USD</b> obtienes acceso ELITE de por vida y participas
                en TODAS las competencias por puntos mensuales del movimiento AURA — sin renovaciones, sin letra chica.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(plans.find((p) => p.code === 'elite')?.prize_categories || []).map((cat) => (
                  <span key={cat} className="text-[11px] px-3 py-1 rounded-full border border-aura-gold/40 bg-aura-gold/10 text-white font-display font-semibold">
                    <Gift size={11} className="inline mr-1 text-aura-gold" /> {cat}
                  </span>
                ))}
              </div>
              <div className="mt-4 text-[11px] text-aura-textDim italic">
                La mecánica exacta para participar la publicará muy pronto el equipo AURA.
              </div>
            </div>

            <div className="hidden md:flex flex-col items-center gap-3">
              <div className="relative">
                <div className="absolute inset-0 rounded-3xl bg-aura-gold/30 blur-2xl" />
                <div className="relative h-32 w-32 rounded-3xl bg-gradient-to-br from-aura-gold via-aura-pink to-aura-violet grid place-items-center shadow-neon-cyan">
                  <Smartphone size={54} className="text-black" strokeWidth={2.2} />
                </div>
              </div>
              <div className="font-mono font-black text-aura-gold text-2xl">$9<span className="text-sm text-aura-textDim ml-1">USD</span></div>
              <div className="text-[10px] uppercase tracking-widest text-aura-textDim">único</div>
            </div>
          </div>
        </section>

        {/* FAQ / trust bar */}
        <section className="grid sm:grid-cols-3 gap-3 text-xs" data-testid="plans-trust">
          <TrustItem icon={<ShieldCheck size={16} className="text-aura-cyan" />} title="Sin renovaciones" desc="ELITE es un pago único de por vida, no una suscripción mensual." />
          <TrustItem icon={<Star size={16} className="text-aura-gold" />} title="Distintivo dorado" desc="Tu perfil y batallas muestran el badge ELITE en todo momento." />
          <TrustItem icon={<Gift size={16} className="text-aura-pink" />} title="Premios reales" desc="Cierre mensual auditado y ganadores anunciados en la app y redes." />
        </section>
      </main>

      {user && <BottomNav />}
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-[10px] uppercase tracking-widest text-aura-textDim">{label}</span>
      <span className="font-mono font-black text-white text-sm">{Number(value || 0).toLocaleString()}</span>
    </div>
  );
}

function TrustItem({ icon, title, desc }) {
  return (
    <div className="aura-card p-4">
      <div className="flex items-center gap-2 mb-1">{icon}<span className="font-display font-bold text-white">{title}</span></div>
      <p className="text-aura-textDim">{desc}</p>
    </div>
  );
}

function PlanCard({ plan, current, icon: Icon, upgrading, onUpgrade, testId }) {
  const isElite = plan.code === 'elite';
  const price = plan.price_usd === 0
    ? { big: 'GRATIS', small: 'para siempre' }
    : { big: `$${plan.price_usd}`, small: 'USD · pago único' };

  return (
    <div
      data-testid={testId}
      className={`aura-card p-6 md:p-7 relative overflow-hidden transition-all ${
        plan.highlighted
          ? 'border-aura-gold/60 shadow-[0_0_60px_-10px_rgba(255,209,102,0.4)]'
          : 'hover:border-white/20'
      }`}
    >
      {plan.highlighted && (
        <div className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-display font-black text-black bg-aura-gold px-2 py-0.5 rounded-full">
          Recomendado
        </div>
      )}
      {current && (
        <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest font-display font-black text-white bg-aura-cyan/25 border border-aura-cyan/40 px-2 py-0.5 rounded-full">
          Tu plan
        </div>
      )}
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full blur-3xl opacity-40"
        style={{ backgroundColor: plan.color }} />
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl border grid place-items-center"
            style={{ borderColor: `${plan.color}55`, backgroundColor: `${plan.color}15`, color: plan.color }}>
            {Icon ? <Icon size={22} /> : <Star size={22} />}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black" style={{ color: plan.color }}>
              PLAN
            </div>
            <div className="font-display font-black text-white text-2xl">{plan.name}</div>
          </div>
        </div>

        <p className="mt-3 text-aura-textDim text-sm">{plan.tagline}</p>

        <div className="mt-6 flex items-end gap-2">
          <div className="font-display font-black text-white text-5xl leading-none">{price.big}</div>
          <div className="text-[11px] text-aura-textDim pb-1">{price.small}</div>
        </div>
        <div className="text-[11px] text-aura-textDim mt-1">{plan.billing}</div>

        <button
          onClick={onUpgrade}
          disabled={current || upgrading}
          data-testid={`btn-select-${plan.code}`}
          className={`mt-6 w-full py-3 rounded-xl font-display font-black tracking-widest text-sm transition ${
            current
              ? 'bg-white/5 text-white/50 cursor-default border border-white/10'
              : isElite
              ? 'bg-gradient-to-r from-aura-gold via-aura-pink to-aura-violet text-black hover:brightness-110'
              : 'bg-aura-gradient text-black hover:brightness-110'
          }`}
        >
          {current ? 'PLAN ACTUAL' : (upgrading ? '…' : plan.cta)}
        </button>

        {/* Benefits */}
        <ul className="mt-6 space-y-2" data-testid={`benefits-${plan.code}`}>
          {plan.benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span
                className="mt-0.5 shrink-0 h-4 w-4 rounded-full grid place-items-center"
                style={{ backgroundColor: `${plan.color}25`, color: plan.color }}
              >
                <Check size={11} strokeWidth={3} />
              </span>
              <span className={i === 0 && isElite ? 'text-white font-display font-bold' : 'text-white/85'}>
                {b}
              </span>
            </li>
          ))}
        </ul>

        {isElite && plan.prize_categories && (
          <div className="mt-5 pt-5 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-gold mb-2">
              Competencias por puntos
            </div>
            <div className="flex flex-wrap gap-1.5">
              {plan.prize_categories.map((c) => (
                <span key={c} className="text-[10px] font-display font-semibold px-2 py-1 rounded-full border border-aura-gold/30 bg-aura-gold/5 text-white">
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
